import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, convertToParamMap, ParamMap, Params, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { NgRedux, select } from '@angular-redux/store';
import { filter, map, take } from 'rxjs/operators';
import { StoreReducers } from '../../../../../store/root.reducer';
import { StoreKeys } from '../../../../models/store-keys';
import { SearchItem } from '../../../../models/search-item';
import { CurrentContextService } from '../../../../redux/current-context/service/current-context.service';
import { DomainItem } from '../../../../models/domain-item';
import { LocaleService } from '../../../../services/locale/locale.service';
import { DomainService } from '../../../../redux/domain/service/domain.service';
import { IAppState } from '../../../../../store/model';
import { InternalRoutes } from '../../../../models/internal-routes';
import { EntitySearchResultState } from '../../../../redux/entity-search/entity-search-result/reducer/entity-search-result.reducer';
import { TaskSearchResultState } from '../../../../redux/task-search/task-search-result/reducer/task-search-result.reducer';
import { DocumentSearchResultState } from '../../../../redux/document-search/document-search-result/reducer/document-search-result.reducer';
import { AbstractSearchItemsService } from '../../../../redux/search-item/service/abstract-search-items.service';
import { AbstractSearchResultService } from '../../../../redux/search-result/service/abstract-search-result.service';
import { DocumentSearchResultService } from '../../../../redux/search-result/service/document-search-result.service';
import { EntitySearchResultService } from '../../../../redux/search-result/service/entity-search-result.service';
import { TaskSearchResultService } from '../../../../redux/search-result/service/task-search-result.service';
import { DocumentSearchItemsService } from '../../../../redux/search-item/service/document-search-item.service';
import { EntitySearchItemsService } from '../../../../redux/search-item/service/entity-search-items.service';
import { TaskSearchItemsService } from '../../../../redux/search-item/service/task-search-item.service';
import { GedCfgFieldService } from '../../../../redux/field/service/ged/ged-cfg-field.service';
import { GedSearchService } from '../../../../../modules/search/service/search/ged/ged-search.service';
import { WkfSearchService } from '../../../../../modules/search/service/search/wkf/wkf-search.service';
import { AbstractSearchService } from '../../../../../modules/search/service/search/abstract-search.service';

/**
 * Documents/Entities/Tasks SearchListResolver
 */
@Injectable({
  providedIn: 'root'
})
export class SearchListResolver implements Resolve<Observable<boolean>> {
  @select([StoreReducers.DYNAMIC_SUB_STORES, StoreKeys.DOMAINS, 'datas']) workspaceItemsList$: Observable<DomainItem[]>;

  private searchResultState$: Observable<DocumentSearchResultState | EntitySearchResultState | TaskSearchResultState>;
  private searchItemsService: AbstractSearchItemsService;
  private searchResultService: AbstractSearchResultService;
  private searchService: AbstractSearchService;
  private currentRoute: InternalRoutes;
  /**
   * Create Documents/Entities SearchListResolver
   *
   */
  constructor(
    private documentSearchResultService: DocumentSearchResultService,
    private entitySearchResultService: EntitySearchResultService,
    private tasksSearchResultService: TaskSearchResultService,
    private documentsSearchItemsService: DocumentSearchItemsService,
    private entitiesSearchItemsService: EntitySearchItemsService,
    private tasksSearchItemsService: TaskSearchItemsService,
    private domainService: DomainService,
    private currentContextService: CurrentContextService,
    private router: Router,
    private gedCfgFieldService: GedCfgFieldService,
    private ngRedux: NgRedux<IAppState>,
    private localeService: LocaleService,
    private gedSearchService: GedSearchService,
    private wkfSearchService: WkfSearchService
  ) {}

  /**
   * Resolve searchList
   *
   * @returns The resolved list of documents
   */
  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.initServicesAndParams(state);
    if (route.queryParamMap && route.queryParamMap.keys.length) {
      // return this.createSearchItemsFromUrl(route.queryParams)
      return this.loadSearchItemsFromQueryParams(route.queryParams)
        .pipe(
          map((isReady: boolean) => {
            if (isReady) {
              const url = state.url;
              const urlPathsWithoutParams: string[] = url.split('?');
              this.router.navigate([urlPathsWithoutParams[0]]);
              return false;
            } else {
              this.router.navigate(['/']);
              return false;
            }
          }),
          take(1)
        );

    } else {
      let searchResultState: DocumentSearchResultState | EntitySearchResultState | TaskSearchResultState;
      let searchResultStoreKey = '';
        switch (this.currentRoute) {
          case InternalRoutes.ENTITIES:
            searchResultStoreKey = StoreKeys.ENTITY_SEARCH_RESULT;
            break;
          case InternalRoutes.DOCUMENTS:
            searchResultStoreKey = StoreKeys.DOCUMENT_SEARCH_RESULT;
            break;
          case InternalRoutes.WORKFLOW:
            searchResultStoreKey = StoreKeys.TASK_SEARCH_RESULT;
            break;
        }
      searchResultState = this.ngRedux.getState().dynamicSubStores[searchResultStoreKey];
      if (!searchResultState || (!searchResultState.datas && searchResultState.loading === false)) {
        this.searchResultService.search();
      }
      return this.searchResultState$.pipe(
        filter(resultState => resultState && resultState.datas && resultState.loading === false && !resultState.error),
        map(value => true),
        take(1)
      );
    }
  }

  private initServicesAndParams(state: RouterStateSnapshot): void {
    let searchResultStorePath: string[];
    this.currentRoute = this.getCurrentRoute(state);
    switch (this.currentRoute) {
      case InternalRoutes.ENTITIES:
        this.searchItemsService = this.entitiesSearchItemsService;
        this.searchResultService = this.entitySearchResultService;
        this.searchService = this.gedSearchService;
        searchResultStorePath = [StoreReducers.DYNAMIC_SUB_STORES, StoreKeys.ENTITY_SEARCH_RESULT];
        break;
      case InternalRoutes.DOCUMENTS:
        this.searchItemsService = this.documentsSearchItemsService;
        this.searchResultService = this.documentSearchResultService;
        this.searchService = this.gedSearchService;
        searchResultStorePath = [StoreReducers.DYNAMIC_SUB_STORES, StoreKeys.DOCUMENT_SEARCH_RESULT];
        break;
      case InternalRoutes.WORKFLOW:
        this.searchItemsService = this.tasksSearchItemsService;
        this.searchResultService = this.tasksSearchResultService;
        this.searchService = this.wkfSearchService;
        searchResultStorePath = [StoreReducers.DYNAMIC_SUB_STORES, StoreKeys.TASK_SEARCH_RESULT];
        break;
    }
    this.searchResultState$ = this.ngRedux.select(searchResultStorePath);
  }

  private getCurrentRoute(state: RouterStateSnapshot): InternalRoutes {
    const urlParts: string[] = state.url.split('/');
    if (urlParts.some((urlPart: string) => urlPart === InternalRoutes.ENTITIES)) {
      return InternalRoutes.ENTITIES;
    } else if (urlParts.some((urlPart: string) => urlPart === InternalRoutes.WORKFLOW)) {
      return InternalRoutes.WORKFLOW;
    } else {
      return InternalRoutes.DOCUMENTS;
    }
  }

  private loadSearchItemsFromQueryParams(queryParams: Params): Observable<boolean> {

    const paramMap: ParamMap = convertToParamMap(queryParams);
    paramMap
      .keys.forEach(key => {
      if (key === '_domains') {
        const domain = paramMap.get(key);
        if (this.domainService.getDomains().some((wsItem: DomainItem) => wsItem.code === domain)) {
          this.currentContextService.setCurrentDomain(domain);
        } else {
          // TODO what do we do when we don't have access at currentDomain ?
          console.error('User has no rights on domain: ', domain);
          return of(false);
        }
      }
    });

    return this.searchService.buildSearchitemsFromQueryParams(queryParams).pipe(
      map((searchItems: SearchItem[]) => {
        this.setSearchItemsToStore(searchItems);
        return true;
      })
    );
  }

  private setSearchItemsToStore(searchItems: SearchItem[]): void {
    this.searchItemsService.removeAllSearchItems();
    searchItems.forEach((searchItem: SearchItem) => {
      this.searchItemsService.addItem(searchItem);
    });
  }


}
